import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ChevronDown, Scan, Shield, AlertTriangle } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/utils'

export interface ScanResultItemData {
  readonly tableName: string
  readonly columnName: string
  readonly dataType: string
  readonly classification: string
  readonly sensitivityLevel: string
  readonly confidence: number
  readonly method?: string
  readonly regulatoryFlags: readonly string[]
  readonly requiresReview: boolean
}

export interface ScanResultsCardData {
  readonly message: string
  readonly results: readonly ScanResultItemData[]
  readonly pagination?: {
    readonly page: number
    readonly totalItems: number
    readonly pageSize: number
  }
}

const LEVEL_STYLES: Record<string, { badge: string; accent: string }> = {
  TOP_SECRET: {
    badge: 'border-red-300 dark:border-red-700 text-red-800 dark:text-red-200 bg-red-100 dark:bg-red-950/50',
    accent: 'border-l-red-500 dark:border-l-red-400',
  },
  RESTRICTED: {
    badge: 'border-orange-300 dark:border-orange-700 text-orange-800 dark:text-orange-200 bg-orange-100 dark:bg-orange-950/50',
    accent: 'border-l-orange-500 dark:border-l-orange-400',
  },
  CONFIDENTIAL: {
    badge: 'border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 bg-amber-100 dark:bg-amber-950/50',
    accent: 'border-l-amber-500 dark:border-l-amber-400',
  },
  INTERNAL: {
    badge: 'border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/50',
    accent: 'border-l-blue-500 dark:border-l-blue-400',
  },
  PUBLIC: {
    badge: 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-950/50',
    accent: 'border-l-gray-400 dark:border-l-gray-600',
  },
}

const DEFAULT_STYLE = LEVEL_STYLES.PUBLIC

const MAX_VISIBLE = 8

export function ScanResultsCard({ data }: { data: ScanResultsCardData }) {
  const [expanded, setExpanded] = useState(false)

  if (data.results.length === 0) {
    return (
      <div className="my-2 rounded-lg border border-border dark:border-border/40 bg-card shadow-sm px-3.5 py-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Scan className="size-4" />
          <span>{data.message}</span>
        </div>
      </div>
    )
  }

  const needsCollapse = data.results.length > MAX_VISIBLE
  const visible = needsCollapse && !expanded ? data.results.slice(0, MAX_VISIBLE) : data.results
  const hiddenCount = data.results.length - MAX_VISIBLE

  // Count by sensitivity
  const sensitivityCounts: Record<string, number> = {}
  for (const r of data.results) {
    sensitivityCounts[r.sensitivityLevel] = (sensitivityCounts[r.sensitivityLevel] ?? 0) + 1
  }

  return (
    <div className="my-2 space-y-1">
      <div className="flex items-center gap-2 px-1 py-1 text-xs text-muted-foreground">
        <Shield className="size-3.5" />
        <span>{data.message}</span>
        <div className="flex gap-1 ml-auto">
          {Object.entries(sensitivityCounts).map(([level, count]) => {
            const style = LEVEL_STYLES[level] ?? DEFAULT_STYLE
            return (
              <Badge key={level} variant="outline" className={cn('text-[9px] px-1 py-0', style.badge)}>
                {level}: {count}
              </Badge>
            )
          })}
        </div>
      </div>
      {visible.map((result, i) => (
        <ScanResultRow key={`${result.tableName}-${result.columnName}-${i}`} result={result} />
      ))}
      {needsCollapse && !expanded && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => setExpanded(true)}
        >
          <ChevronDown className="size-3.5" />
          Show {hiddenCount} more results
        </Button>
      )}
    </div>
  )
}

function ScanResultRow({ result }: { result: ScanResultItemData }) {
  const style = LEVEL_STYLES[result.sensitivityLevel] ?? DEFAULT_STYLE
  const isHighSensitivity = ['TOP_SECRET', 'RESTRICTED', 'CONFIDENTIAL'].includes(result.sensitivityLevel)

  return (
    <div className={cn(
      'rounded-lg border border-border dark:border-border/40 bg-card shadow-sm overflow-hidden border-l-[3px]',
      style.accent
    )}>
      <div className="flex items-center gap-3 px-3.5 py-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono font-medium text-foreground">
              {result.tableName}.{result.columnName}
            </span>
            <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 shrink-0 uppercase font-semibold', style.badge)}>
              {result.sensitivityLevel}
            </Badge>
            {result.requiresReview && (
              <AlertTriangle className="size-3 text-amber-500 shrink-0" />
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-[11px] text-muted-foreground">
            <span>{result.classification}</span>
            <span className="font-mono">{result.dataType}</span>
            {result.method && <span>{result.method}</span>}
            <span>{Math.round(result.confidence * 100)}% confidence</span>
          </div>
        </div>
        {isHighSensitivity && result.regulatoryFlags.length > 0 && (
          <div className="flex flex-wrap gap-1 shrink-0">
            {result.regulatoryFlags.map((flag) => (
              <Badge key={flag} variant="secondary" className="text-[9px] px-1 py-0 font-mono">
                {flag}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
